// Scène de pratique dessinée sur un canvas : notes descendantes, lignes de mesure,
// ligne de frappe et clavier. Aucune dépendance à React : la page appelle drawStage
// à chaque image avec l'état courant.

import type { NoteState } from '../../engine/session';
import { allNotes, ticksPerBeat, ticksPerMeasure, pitchRange, type Hand, type Score } from '../../music/model';
import { isBlackKey, noteName, pitchClass, type NamingSystem } from '../../music/pitchNames';

export interface StageTheme {
  background: string;
  laneWhite: string;
  laneBlack: string;
  beatLine: string;
  measureLine: string;
  text: string;
  textDim: string;
  right: string;
  left: string;
  correct: string;
  wrong: string;
  timing: string;
  uncertain: string;
  expected: string;
  hitLine: string;
  keyWhite: string;
  keyBlack: string;
  keyBorder: string;
  keyText: string;
  keyTextBlack: string;
  middleC: string;
  /** Texte écrit sur les notes descendantes (nom, point d'interrogation). */
  noteText: string;
  /** Pastille du doigté sur une note. */
  fingerBackground: string;
  fingerText: string;
}

export const DEFAULT_THEME: StageTheme = {
  background: '#fbf7ef',
  laneWhite: 'rgba(20,184,176,0.07)',
  laneBlack: 'rgba(38,41,46,0.06)',
  beatLine: 'rgba(38,41,46,0.09)',
  measureLine: 'rgba(38,41,46,0.24)',
  text: '#26292e',
  textDim: '#8a8f98',
  right: '#14b8b0',
  left: '#8b6ee0',
  correct: '#1f9d5f',
  wrong: '#e5484d',
  timing: '#e69a1a',
  uncertain: '#9aa5b1',
  expected: '#26292e',
  hitLine: '#ff6b57',
  keyWhite: '#ffffff',
  keyBlack: '#2b2f36',
  keyBorder: '#cfc5b3',
  keyText: '#5b6270',
  keyTextBlack: '#e6e8ec',
  middleC: '#ff6b57',
  noteText: '#ffffff',
  fingerBackground: 'rgba(255,255,255,0.92)',
  fingerText: '#26292e',
};

export interface KeyFlash {
  pitch: number;
  color: string;
  until: number;
}

export interface StageState {
  score: Score;
  hands: readonly Hand[];
  /** Position de lecture en ticks. */
  tick: number;
  now: number;
  noteStates: ReadonlyMap<string, NoteState>;
  /** Touches attendues (mode attente) à mettre en évidence. */
  expectedPitches: readonly number[];
  /** Touches enfoncées ou notes en cours de détection : hauteur → couleur. */
  heldPitches: ReadonlyMap<number, string>;
  flashes: readonly KeyFlash[];
  naming: NamingSystem;
  showNames: boolean;
  showFingers: boolean;
  waiting: boolean;
  /** Dessiner les notes descendantes (faux : clavier seul). */
  showNotes: boolean;
}

export interface StageLayout {
  width: number;
  height: number;
  keyboardTop: number;
  keyboardHeight: number;
  lowPitch: number;
  highPitch: number;
  whiteWidth: number;
  blackWidth: number;
  blackHeight: number;
  pxPerTick: number;
  whiteIndex: Map<number, number>;
}

function countWhite(low: number, high: number): number {
  let n = 0;
  for (let p = low; p <= high; p++) if (!isBlackKey(p)) n++;
  return n;
}

export function computeLayout(score: Score, hands: readonly Hand[], width: number, height: number, options: { keyboardOnly?: boolean } = {}): StageLayout {
  const range = pitchRange(score, hands);
  // Tessiture élargie à des octaves entières (de do à si), au moins deux octaves.
  let low = Math.floor((range.min - 1) / 12) * 12;
  let high = Math.ceil((range.max + 2) / 12) * 12 - 1;
  while (high - low < 23) {
    if (low > 21) low -= 12;
    else high += 12;
  }
  // Sur un écran étroit, on se limite à la tessiture du morceau élargie d'une touche blanche.
  if (width / countWhite(low, high) < 20) {
    low = range.min - 2;
    high = range.max + 2;
    while (isBlackKey(low)) low--;
    while (isBlackKey(high)) high++;
  }
  const whiteCount = countWhite(low, high);
  const whiteWidth = width / whiteCount;
  const keyboardHeight = options.keyboardOnly ? Math.max(40, height - 6) : Math.max(56, Math.min(150, whiteWidth * 4.4, height * 0.28));
  const keyboardTop = height - keyboardHeight;
  const whiteIndex = new Map<number, number>();
  let i = 0;
  for (let p = low; p <= high; p++) {
    if (!isBlackKey(p)) whiteIndex.set(p, i++);
  }
  const visibleBeats = height < 420 ? 4.5 : 6;
  const pxPerTick = Math.max(keyboardTop, 1) / (visibleBeats * ticksPerBeat(score));
  return {
    width,
    height,
    keyboardTop,
    keyboardHeight,
    lowPitch: low,
    highPitch: high,
    whiteWidth,
    blackWidth: whiteWidth * 0.58,
    blackHeight: keyboardHeight * 0.62,
    pxPerTick,
    whiteIndex,
  };
}

/** Position horizontale et largeur de la touche d'une hauteur. */
export function keyRect(layout: StageLayout, pitch: number): { x: number; w: number } | null {
  if (pitch < layout.lowPitch || pitch > layout.highPitch) return null;
  if (!isBlackKey(pitch)) {
    const i = layout.whiteIndex.get(pitch);
    if (i === undefined) return null;
    return { x: i * layout.whiteWidth, w: layout.whiteWidth };
  }
  const leftWhite = layout.whiteIndex.get(pitch - 1);
  if (leftWhite === undefined) return null;
  return { x: (leftWhite + 1) * layout.whiteWidth - layout.blackWidth / 2, w: layout.blackWidth };
}

/** Hauteur de la touche sous un point du canvas (clavier d'essai au clic), ou null. */
export function pitchAtPoint(layout: StageLayout, x: number, y: number): number | null {
  if (y < layout.keyboardTop || y > layout.height) return null;
  const inBlackZone = y < layout.keyboardTop + layout.blackHeight;
  if (inBlackZone) {
    for (let p = layout.lowPitch; p <= layout.highPitch; p++) {
      if (!isBlackKey(p)) continue;
      const r = keyRect(layout, p);
      if (r && x >= r.x && x < r.x + r.w) return p;
    }
  }
  const i = Math.floor(x / layout.whiteWidth);
  for (const [p, idx] of layout.whiteIndex) if (idx === i) return p;
  return null;
}

function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`;
}

function noteColor(state: NoteState, hand: Hand, theme: StageTheme): { fill: string; stroke: string; alpha: number } {
  const base = hand === 'right' ? theme.right : theme.left;
  switch (state) {
    case 'correct':
      return { fill: theme.correct, stroke: theme.correct, alpha: 0.85 };
    case 'early':
    case 'late':
      return { fill: theme.timing, stroke: theme.timing, alpha: 0.85 };
    case 'missed':
    case 'wrong':
      return { fill: theme.wrong, stroke: theme.wrong, alpha: 0.5 };
    case 'uncertain':
      return { fill: theme.uncertain, stroke: theme.uncertain, alpha: 0.6 };
    default:
      return { fill: base, stroke: base, alpha: 0.92 };
  }
}

export function drawStage(ctx: CanvasRenderingContext2D, layout: StageLayout, state: StageState, theme: StageTheme = DEFAULT_THEME): void {
  const { width, height, keyboardTop, pxPerTick } = layout;
  const score = state.score;
  const tpb = ticksPerBeat(score);
  const tpm = ticksPerMeasure(score);

  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, width, height);

  // Couloirs des touches.
  for (let p = layout.lowPitch; p <= layout.highPitch && state.showNotes; p++) {
    const r = keyRect(layout, p);
    if (!r) continue;
    if (isBlackKey(p)) {
      ctx.fillStyle = theme.laneBlack;
      ctx.fillRect(r.x, 0, r.w, keyboardTop);
    } else if (pitchClass(p) === 0 || pitchClass(p) === 5) {
      ctx.fillStyle = theme.laneWhite;
      ctx.fillRect(r.x, 0, r.w, keyboardTop);
    }
  }

  // Lignes de temps et de mesure (les ticks au-dessus de la ligne sont dans le futur).
  const ticksVisible = keyboardTop / pxPerTick;
  const firstBeat = Math.floor(state.tick / tpb) * tpb;
  ctx.lineWidth = 1;
  ctx.font = '600 12px "Source Sans 3", "Segoe UI", system-ui, sans-serif';
  ctx.textBaseline = 'bottom';
  for (let t = firstBeat; state.showNotes && t <= state.tick + ticksVisible + tpb; t += tpb) {
    const y = keyboardTop - (t - state.tick) * pxPerTick;
    if (y < 0 || y > keyboardTop) continue;
    const isMeasure = ((t % tpm) + tpm) % tpm === 0;
    ctx.strokeStyle = isMeasure ? theme.measureLine : theme.beatLine;
    ctx.beginPath();
    ctx.moveTo(0, Math.round(y) + 0.5);
    ctx.lineTo(width, Math.round(y) + 0.5);
    ctx.stroke();
    if (isMeasure && t >= 0) {
      ctx.fillStyle = theme.textDim;
      ctx.textAlign = 'left';
      ctx.fillText(String(Math.round(t / tpm) + 1), 6, y - 3);
    }
  }

  // Notes descendantes.
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, width, keyboardTop);
  ctx.clip();
  const pulse = 0.55 + 0.45 * Math.sin(state.now / 160);
  for (const note of state.showNotes ? allNotes(score, state.hands) : []) {
    const r = keyRect(layout, note.pitch);
    if (!r) continue;
    const yBottom = keyboardTop - (note.start - state.tick) * pxPerTick;
    const h = Math.max(6, note.duration * pxPerTick - 3);
    const yTop = yBottom - h;
    if (yTop > keyboardTop || yBottom < 0) continue;
    const st = state.noteStates.get(note.id) ?? 'pending';
    const c = noteColor(st, note.hand, theme);
    const x = r.x + 1.5;
    const w = r.w - 3;
    ctx.globalAlpha = c.alpha;
    ctx.fillStyle = c.fill;
    roundRect(ctx, x, yTop, w, h, Math.min(5, w / 3));
    ctx.fill();
    ctx.globalAlpha = 1;
    const isExpected = state.waiting && st === 'pending' && state.expectedPitches.includes(note.pitch) && Math.abs(note.start - state.tick) < 1;
    if (isExpected) {
      ctx.strokeStyle = withAlpha(theme.expected, pulse);
      ctx.lineWidth = 2;
      roundRect(ctx, x - 1, yTop - 1, w + 2, h + 2, Math.min(6, w / 3));
      ctx.stroke();
      ctx.lineWidth = 1;
    }
    if (st === 'uncertain' && h >= 14 && w >= 12) {
      ctx.fillStyle = theme.noteText;
      ctx.font = '700 13px "Source Sans 3", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('?', x + w / 2, yTop + h / 2);
    } else if (state.showNames && h >= 16 && w >= 13) {
      ctx.fillStyle = theme.noteText;
      ctx.font = `600 ${w >= 22 ? 12 : 10}px "Source Sans 3", system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(noteName(note.pitch, state.naming), x + w / 2, yBottom - 3);
    }
    if (state.showFingers && note.finger && h >= 30 && w >= 18) {
      ctx.fillStyle = theme.fingerBackground;
      ctx.beginPath();
      ctx.arc(x + w / 2, yTop + 10, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = theme.fingerText;
      ctx.font = '700 10px "Source Sans 3", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(note.finger), x + w / 2, yTop + 10.5);
    }
  }
  ctx.restore();

  // Ligne de frappe.
  if (state.showNotes) {
    ctx.strokeStyle = theme.hitLine;
    ctx.lineWidth = 2;
    ctx.shadowColor = theme.hitLine;
    ctx.shadowBlur = state.waiting ? 10 : 4;
    ctx.beginPath();
    ctx.moveTo(0, keyboardTop);
    ctx.lineTo(width, keyboardTop);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.lineWidth = 1;
  }

  // Clavier : touches blanches puis touches noires.
  const flashFor = (pitch: number): { color: string; alpha: number } | null => {
    let best: KeyFlash | null = null;
    for (const f of state.flashes) if (f.pitch === pitch && f.until > state.now && (!best || f.until > best.until)) best = f;
    if (!best) return null;
    return { color: best.color, alpha: Math.max(0.15, Math.min(1, (best.until - state.now) / 450)) };
  };
  for (const black of [false, true]) {
    for (let p = layout.lowPitch; p <= layout.highPitch; p++) {
      if (isBlackKey(p) !== black) continue;
      const r = keyRect(layout, p);
      if (!r) continue;
      const kh = black ? layout.blackHeight : layout.keyboardHeight;
      const y = keyboardTop;
      ctx.fillStyle = black ? theme.keyBlack : theme.keyWhite;
      ctx.fillRect(r.x, y, r.w, kh);
      ctx.strokeStyle = theme.keyBorder;
      ctx.strokeRect(r.x + 0.5, y + 0.5, r.w - 1, kh - 1);
      const held = state.heldPitches.get(p);
      const expected = state.waiting && state.expectedPitches.includes(p);
      const flash = flashFor(p);
      if (expected) {
        ctx.fillStyle = withAlpha(theme.hitLine, 0.35 + 0.35 * pulse);
        ctx.fillRect(r.x + 1, y + 1, r.w - 2, kh - 2);
      }
      if (held) {
        ctx.fillStyle = withAlpha(held, 0.55);
        ctx.fillRect(r.x + 1, y + 1, r.w - 2, kh - 2);
      }
      if (flash) {
        ctx.fillStyle = withAlpha(flash.color, 0.75 * flash.alpha);
        ctx.fillRect(r.x + 1, y + 1, r.w - 2, kh - 2);
      }
      if (!black && state.showNames && r.w >= 16) {
        const isMiddleC = p === 60;
        ctx.fillStyle = isMiddleC ? theme.middleC : theme.keyText;
        ctx.font = `${isMiddleC ? 700 : 600} ${r.w >= 26 ? 12 : 10}px "Source Sans 3", system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(noteName(p, state.naming), r.x + r.w / 2, y + kh - 6);
        if (isMiddleC) {
          ctx.beginPath();
          ctx.arc(r.x + r.w / 2, y + kh - 24, 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}
